<template>
  <div class="row d-flex justify-content-center">
    <div class="col-md-12 mr-auto ml-auto modal-overflow">
      <ProjectAndJobWizard ref="wizard" :key="wizardKey" @closeModal="goToProjectAndClose">
        <wizard-tab id="project-search" @active-toggled="setNextButtonActiveState">
          <template slot="label">
            <i class="nc-icon nc-zoom-split"></i>
            Project Search
          </template>
          <project-search ref="projectSearch" @projectSelected="projectSelected" @createProject="createProject"></project-search>
        </wizard-tab>

        <wizard-tab id="create-project" :before-change="() => maybeValidateAndCreateProject()" v-if="renderCreateProjectTab" @active-toggled="setNextButtonActiveState">
          <template slot="label">
            <i class="nc-icon nc-tile-56"></i>
            Create Project
          </template>
          <create-project ref="createProject" :data="{showButtons: false, suppressCreateProjectNotification: true, source: constants.CREATE_PROJECT.FROM_PROJECT_SEARCH_WIZARD}"></create-project>
        </wizard-tab>

        <wizard-tab id="select-workflow" :before-change="() => selectTemplateOrWorkflow()" @active-toggled="setNextButtonActiveState">
          <template slot="label">
            <i class="nc-icon nc-ruler-pencil"></i>
            Job Template OR Workflow
          </template>
          <select-template-or-workflow ref="selectTemplateOrWorkflow" :data="{projectDepartments: projectDepartments}"></select-template-or-workflow>
        </wizard-tab>

        <wizard-tab id="create-job" :before-change="() => checkClientAndPoRequiredAndSave()">
          <template slot="label">
            <i class="nc-icon nc-check-2"></i>
            Create Job
          </template>
          <add-job ref="addJob"></add-job>
        </wizard-tab>
      </ProjectAndJobWizard>
    </div>
  </div>
</template>

<script>
  import {notifySuccess, notifyError, sleepOneSecond} from 'src/utilities/utils'
  import {jobCheckClientAndPoRequiredAndSave, jobValidateContactEmails} from 'src/utilities/jobHelper'
  import Constants from 'src/utilities/constants'
  import CreateProject from '../CreateProject'
  import ProjectSearch from './ProjectSearch'
  import SelectTemplateOrWorkflow from 'src/components/Dashboard/Views/Jobs/Modal/SelectTemplateOrWorkflow'
  import AddJob from 'src/components/Dashboard/Views/Jobs/Modal/AddJob'
  import ProjectAndJobWizard from 'src/components/UIComponents/Wizard/ProjectAndJobWizard'
  import WizardTab from 'src/components/UIComponents/Wizard/WizardTab'
  import {CREATED, OK} from 'http-status-codes'
  import _ from 'lodash'
  import {ptracPost} from '@/utilities/ptracApi'

  const CREATE = 'create'

  export default {
    props: ['data'],
    components: {
      AddJob,
      CreateProject,
      ProjectSearch,
      SelectTemplateOrWorkflow,
      ProjectAndJobWizard,
      WizardTab
    },
    data() {
      return {
        purchaseOrderNumber: null,
        constants: Constants,
        wizardModel: {},
        wizardKey: 0,
        isProcessing: false,
        projectDepartments: [],
        renderCreateProjectTab: false
      }
    },
    methods: {
      setNextButtonActiveState(payload) {
        if (payload.active) {
          if (payload.id === 'select-workflow') {
            this.$store.commit('wizard/nextDisabled', true)
            if (this.$store.get('jobs/create@processDefinitionKey')) {
              this.$store.commit('wizard/nextDisabled', false)
            }
          } else if (payload.id === 'project-search') {
            this.$store.commit('wizard/nextDisabled', true)
            if (this.$store.get('jobs/create@projectId')) {
              this.$store.commit('wizard/nextDisabled', false)
            }
          } else if (payload.id === 'create-project') {
            this.$store.commit('wizard/nextDisabled', false)
          }
        }
      },
      goToProjectAndClose() {
        const projectId = this.$store.get('projects/edit/projectId')
        if (this.$route.path !== `/projects/${projectId}`) {
          this.$router.push({path: `/projects/${projectId}`})
        }
        this.$emit('close')
      },
      forceRerender() {
        this.wizardKey++
      },
      selectTemplateOrWorkflow() {
        return !!this.$store.get('jobs/create@processDefinitionKey');
      },
      async maybeValidateAndCreateProject() {
        if (this.renderCreateProjectTab) {
          if (await this.$refs.createProject.validateAndSubmit()) {
            this.$store.commit('wizard/nextDisabled', false)
            this.renderCreateProjectTab = false
            this.forceRerender()
            setTimeout(() => {
              if (this.$refs && this.$refs.wizard) {
                this.$refs.wizard.activeTabIndex++
              }
            }, Constants.TIME.MILLIS.HALF_SECOND)
          }
        }
      },
      async loadElasticsearchResults(jobId) {
        // try up to 10 times for an Elasticsearch search result, with 1 second search interval
        for (let i = 0; i < 10; i++) {
          if (await this.loadElasticsearchTasks(jobId)) {
            return true
          }
          await sleepOneSecond()
        }
        return false
      },
      async loadPtracJobTaskResults(jobId) {
        // try up to 10 times for PTRAC to return tasks for the newly created job, with 1 second search interval
        for (let i = 0; i < 10; i++) {
          const ptracTasks = await this.loadPtracTasks(jobId)
          if (ptracTasks.length > 0) {
            return ptracTasks
          }
          await sleepOneSecond()
        }
        return null
      },
      async checkClientAndPoRequiredAndSave() {
        await jobCheckClientAndPoRequiredAndSave(this, CREATE)
      },
      async validateContactEmails() {
        await jobValidateContactEmails(this, CREATE)
      },
      async validateJobDetailsAndSubmit() {
        if (await this.$refs.addJob.validate() && !this.isProcessing) {
          this.isProcessing = true
          const job = this.$store.get('jobs/create')
          // hacky workaround - ckEditor having issues with null description in vuex
          job.description = this.$refs.addJob.localDescription

          const response = await ptracPost('jobs/create-job-and-initiate-process-instance', job)

          if (CREATED === response.status) {
            const createdJob = response.data
            if (createdJob.status === Constants.STATUSES.ERROR) {
              notifyError(this, 'Job created but with errors. Please inspect error log before using this Job')
            } else {
              notifySuccess(this, 'Job Created Successfully')
            }

            if (this.purchaseOrderNumber) {
              await this.addPoNumberToInvoices(createdJob.id)
            }

            await this.resetCreateJobState(createdJob.projectId)
            this.isProcessing = false

            // route to task search
            if (this.$store.get('wizard/finishAndViewTasks')) {
              await this.redirectToTaskSearchResults(createdJob)
            } else if (this.$store.get('wizard/finishAndEditTasks')) {
              await this.$store.dispatch ('jobs/fetchByProjectId', createdJob.projectId)
              if (this.$route.path === '/projects/search') {
                // DO NOT add `await` to the router.push call below - breaks opening the Bulk Edit Tasks modal
                this.$router.push({path: `/projects/${createdJob.projectId}`})
              }
              await this.closeWizardAndViewEditTasksModal(createdJob)
              this.$emit('close')
            } else { // stay on - /#/projects/search
              this.$emit('close')
            }
          } else {
            notifyError(this, 'Failed to Create Job', response.errors)
          }
        }
      },
      async resetCreateJobState(projectId) {
        this.$refs.addJob.resetLocalState()
        await this.$store.dispatch('jobs/fetchByProjectId', projectId)
        this.$store.commit('jobs/resetCreate')
      },
      async closeWizardAndViewEditTasksModal(job) {
        if (await this.loadPtracJobTaskResults(job.id)) {
          this.$eventHub.$emit('show-modal-2', 'Bulk Edit Tasks', {jobId: job.id}, '95%')
        }
      },
      async redirectToTaskSearchResults(job) {
        // give monstache a moment to sync tasks from Mongo to Elasticsearch
        const data = {
          message: 'Loading tasks... please wait a moment',
          seconds: Constants.TIME.MILLIS.TEN_SECONDS // timeout setting
        }
        this.$eventHub.$emit('show-modal-1', 'Please Wait', data, '600px')

        if (await this.loadElasticsearchResults(job.id)) {
          this.$eventHub.$emit('closePleaseWait')
          this.goSeeTasks(job.id)
        } else {
          notifyError(this, 'Failed to load tasks. Please wait a moment and try again using the "See Tasks" button in the Job table.')
        }

        this.$store.commit('wizard/finishAndViewTasks', false)
      },
      async loadPtracTasks(jobId) {
        const response = await this.$store.dispatch('tasks/fetchTasksByJobId', jobId)
        if (OK === response.status && response.data.length > 0) {
          return response.data
        }
        return null
      },
      async loadElasticsearchTasks(jobId) {
        this.$store.set('tasks/search/query@status', [])
        this.$store.set('tasks/search/query@jobId', jobId)
        const response = await this.$store.dispatch('tasks/search/fetchSearchResults', 1)
        return response && response.hits && response.hits.hits && response.hits.hits.length > 0
      },
      goSeeTasks(jobId) {
        this.$store.commit('tasks/search/resetQuery')
        this.$store.commit('tasks/search/resetResults')
        this.$router.push({path: `/tasks/search?jobId=${jobId}`})
        this.$emit('close')
      },
      async jobClientOrPoChecksComplete(poNumber) {
        if (poNumber) {
          this.purchaseOrderNumber = poNumber
        }
        await this.validateContactEmails()
      },
      async addPoNumberToInvoices(jobId) {
        const tasks = await this.loadPtracJobTaskResults(jobId)
        if (!tasks || tasks.length === 0) {
          return
        }

        const invoiceTasks = tasks.filter(task => task.name.startsWith(Constants.TASKS.NAMES.INVOICE))

        for (const invoiceTask of invoiceTasks) {
          invoiceTask.fieldValues.poNumber = this.purchaseOrderNumber

          // don't use save2 - sometimes tasks are returned before they are fully setup and associated with their complimentary Camunda task
          // PATCH allows updating just the fields needed rather than the whole object.
          const response = await this.$store.dispatch('mergePatch2', {
            id: invoiceTask.id,
            endpoint: 'tasks',
            data: { fieldValues: invoiceTask.fieldValues}
          })

          if (OK === response.status) {
            notifySuccess(this, 'Purchase Order Number successfully added to Invoice Task.')
          } else {
            notifyError(this, 'Unable to update Invoice Task. Please input Purchase Order data manually.')
          }
        }

        this.purchaseOrderNumber = null
      },
      createProject() {
        this.renderCreateProjectTab = true
        this.forceRerender()
        this.$store.commit('wizard/nextDisabled', false)
        setTimeout(() => {
          if (this.$refs && this.$refs.wizard) {
            this.$refs.wizard.activeTabIndex++
            this.$eventHub.$emit('copyQueryToCreate')
          }
        }, Constants.TIME.MILLIS.QUARTER_SECOND)
      },
      setProjectAndDepartment(project) {
        this.$store.set('jobs/create@projectId', project.id)

        this.projectDepartments = project.departments

        const currentUser = this.$store.getters['security/user']
        let departmentId = _.get(currentUser, 'preferences.global.defaultDepartment')

        if (project.departments.length > 0) {
          departmentId = project.departments[0]
        }

        if (departmentId) {
          this.$store.set('jobs/create@departmentId', departmentId)
        }
      },
      async projectSelected(project) {
        this.renderCreateProjectTab = false
        await this.setProjectAndDepartment(project)
        this.$store.commit('wizard/nextDisabled', false)
      },
      disableNext() {
        this.$store.commit('wizard/nextDisabled', true)
      }
    },
    mounted() {
      this.$eventHub.$on('copyQueryToCreateWizard', this.createProject)
      this.$store.commit('projects/edit/details/reset')
      this.$store.commit('projects/edit/address/reset')
      this.$eventHub.$on('jobClientOrPoChecksComplete', this.jobClientOrPoChecksComplete)
      this.$eventHub.$on('doNotAddContactEmail', this.validateJobDetailsAndSubmit)

      this.disableNext()
    },
    destroyed() {
      this.$store.commit('projects/searchWizard/resetQuery')
      this.$store.commit('projects/searchWizard/resetResults')
      this.$store.commit('wizard/finishAndViewTasks', false)
      this.$store.commit('wizard/finishAndEditTasks', false)
      this.$eventHub.$off('copyQueryToCreateWizard')
      this.$eventHub.$off('jobClientOrPoChecksComplete')
      this.$eventHub.$off('doNotAddContactEmail')
    }
  }
</script>

<style scoped>
  .vue-form-wizard .wizard-icon-circle.tab_shape {
    background-color: #9A9A9A !important;
    color: white;
  }
  .card {
    box-shadow: none;
  }
  .tab-content-min-height {
    min-height: 150px;
  }
</style>
